import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast as showGlobalToast } from '@/utils/toast';
import { createTaskFromDetailToolBridge } from '@/pages/ipe/workbench/utils/createTaskFromDetailToolBridge';
import {
  notifyQuickBridgeTaskCreated,
  showPlatformListingIngestStartedToast,
} from '@/pages/ipe/workbench/utils/showTaskCreatedDetailToast';
import { loadImageTemplatesForDetailBridge } from '@/pages/ipe/workbench/utils/detailToolBridgeActions';
import {
  getDefaultDetailToolBridgeParams,
  mergeDetailToolBridgeParams,
} from '@/pages/ipe/workbench/utils/detailToolBridgeDefaultParams';
import useDetailGoodsTemplateBridge from '@/pages/ipe/workbench/hooks/useDetailGoodsTemplateBridge';
import useDetailAiCopywritingBridge from '@/pages/ipe/workbench/hooks/useDetailAiCopywritingBridge';
import useListingAutoRulePick from '@/pages/ipe/workbench/hooks/useListingAutoRulePick';
import {
  ingestDetailBridgeSourcesToPlatformListingProgressive,
  estimateDetailBridgeIngestItemCount,
  navigateAfterPlatformListingIngest,
} from '@/pages/ipe/workbench/utils/ingestDetailBridgeSourcesToPlatformListing';
import {
  clearListingIngestPending,
  completeListingIngestPending,
  emitListingIngestProgress,
  markListingIngestPending,
  migrateListingIngestPending,
  reviseListingIngestPendingTotal,
  updateListingIngestPendingProgress,
} from '@/pages/ipe/workbench/utils/listingIngestProgress';
import {
  collectSourcingRowsFromBridgeSources,
  promoteSourcingRowsToPlatformListingProgressive,
} from '@/pages/ipe/workbench/utils/sourcingToolBridge';
import {
  canUsePlatformListingQuickBridge,
  PLATFORM_LISTING_QUICK_BRIDGE_NAV_IDS,
} from '@/pages/ipe/workbench/constants/sourcingToolBridgeConfig';
import { resolveListingBridgeSourceLabel } from '@/pages/ipe/workbench/utils/listingBridgeSourceLabel';
import i18n from '@/i18n';

function createListingIngestLifecycle(navigate, { provisionalKey = '', recolor = false } = {}) {
  let batchKey = String(provisionalKey || '').trim();
  return {
    getBatchKey: () => batchKey,
    callbacks: {
      onBatchReady: ({ batchKey: bk, expectedCount = 0, mode = 'ingest', sourceLabel = '' } = {}) => {
        const realKey = String(bk || '').trim();
        if (!realKey) return;
        if (batchKey && batchKey !== realKey) {
          migrateListingIngestPending(batchKey, realKey, {
            mode,
            total: expectedCount,
            sourceLabel,
          });
        } else {
          markListingIngestPending(realKey, {
            mode,
            total: expectedCount,
            sourceLabel,
          });
        }
        batchKey = realKey;
        showPlatformListingIngestStartedToast({ navigate, batchKey: realKey, recolor });
        // 复色：批次一建好就跳商品库，等入库完成后本页自动弹复色弹窗
        if (recolor) {
          navigateAfterPlatformListingIngest({ navigate, batchKey: realKey, recolor: true });
        }
      },
      onProgress: ({ batchKey: bk, done = 0, total = 0 } = {}) => {
        const key = String(bk || batchKey || '').trim();
        if (key) updateListingIngestPendingProgress(key, done, total);
        emitListingIngestProgress(key || batchKey);
      },
      onComplete: ({ batchKey: bk, count } = {}) => {
        const key = String(bk || batchKey || '').trim();
        if (key) {
          completeListingIngestPending(key, Number(count) || 0);
        }
        emitListingIngestProgress(key || batchKey);
      },
    },
  };
}

async function runDetailBridgeListingIngest({
  ingestPayload = null,
  sourcingRows = [],
  navigate,
  provisionalKey = '',
  bindListingAutoRule = false,
  listingAutoRuleId = null,
  listingAutoRuleIds = null,
  recolor = false,
}) {
  const ruleIds = Array.isArray(listingAutoRuleIds) && listingAutoRuleIds.length > 0
    ? listingAutoRuleIds
    : (listingAutoRuleId != null ? [listingAutoRuleId] : []);
  const primaryRuleId = ruleIds[0] ?? listingAutoRuleId ?? null;
  // 绑定了规则就要补刊登。TEMU 首条由后台先建任务，这里带上批次号去补图片/视频转换。
  const shouldPublishExtraRules = !recolor && bindListingAutoRule && ruleIds.length > 0;

  if (sourcingRows.length > 0) {
    const lifecycle = createListingIngestLifecycle(navigate, { recolor });
    try {
      const result = await promoteSourcingRowsToPlatformListingProgressive(sourcingRows, {
        ...lifecycle.callbacks,
        bindListingAutoRule,
        listingAutoRuleId: primaryRuleId,
      });
      if (shouldPublishExtraRules) {
        const sourceItems = (Array.isArray(result?.ingestedRows) ? result.ingestedRows : sourcingRows)
          .filter((row) => row?.id != null);
        const { publishExtraAutoRulesAfterIngest } = await import(
          '@/pages/ipe/workbench/utils/publishItemsWithListingAutoRules'
        );
        void publishExtraAutoRulesAfterIngest({
          items: sourceItems,
          sourcingItemIds: sourceItems.map((row) => row.id),
          listingAutoRuleIds: ruleIds,
          primaryRuleId,
          remarkPrefix: '快捷入库多规则上架',
          batchKey: lifecycle.getBatchKey?.() || result?.batchKey || '',
        });
      }
    } catch (error) {
      const key = lifecycle.getBatchKey();
      if (key) clearListingIngestPending(key);
      throw error;
    }
    return;
  }

  if (!ingestPayload) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  const sourceLabel = resolveListingBridgeSourceLabel(ingestPayload?.sourceNavId) || '快捷入库';
  const estimateCount = Math.max(
    estimateDetailBridgeIngestItemCount(ingestPayload?.sources || []),
    1,
  );
  const initialProvisionalKey = String(provisionalKey || '').trim()
    || `__prep__${Date.now()}`;
  if (!provisionalKey) {
    markListingIngestPending(initialProvisionalKey, {
      mode: 'ingest',
      total: estimateCount,
      sourceLabel,
      provisional: true,
    });
    emitListingIngestProgress(initialProvisionalKey);
  } else {
    reviseListingIngestPendingTotal(initialProvisionalKey, estimateCount);
  }
  const ingestLifecycle = createListingIngestLifecycle(navigate, {
    provisionalKey: initialProvisionalKey,
    recolor,
  });
  try {
    const result = await ingestDetailBridgeSourcesToPlatformListingProgressive({
      ...ingestPayload,
      bindListingAutoRule,
      listingAutoRuleId: primaryRuleId,
    }, {
      onBatchReady: ({ batchKey, expectedCount }) => {
        ingestLifecycle.callbacks.onBatchReady({
          batchKey,
          expectedCount,
          mode: 'ingest',
          sourceLabel,
        });
      },
      onProgress: ({ batchKey, done, total }) => {
        ingestLifecycle.callbacks.onProgress({ batchKey, done, total });
      },
      onComplete: ({ batchKey, count }) => {
        ingestLifecycle.callbacks.onComplete({ batchKey, count });
      },
    });
    if (shouldPublishExtraRules) {
      const sourceItems = (Array.isArray(result?.ingestedRows) ? result.ingestedRows : [])
        .filter((row) => row?.id != null);
      const { publishExtraAutoRulesAfterIngest } = await import(
        '@/pages/ipe/workbench/utils/publishItemsWithListingAutoRules'
      );
      void publishExtraAutoRulesAfterIngest({
        items: sourceItems,
        sourcingItemIds: sourceItems.map((row) => row.id),
        listingAutoRuleIds: ruleIds,
        primaryRuleId,
        remarkPrefix: '快捷入库多规则上架',
        batchKey: result?.batchKey || ingestLifecycle.getBatchKey?.() || '',
      });
    }
  } catch (error) {
    const key = ingestLifecycle.getBatchKey();
    if (key) clearListingIngestPending(key);
    throw error;
  }
}

/** 详情页快捷写入商品库：跳过确认弹窗，先建批次再后台逐条入库 */
async function runPlatformListingIngestInBackground({
  resolveLaunchContext,
  bridgeSourceNavId,
  navigate,
  toast,
  resolveForIngest,
  recolor = false,
}) {
  const sourceLabel = resolveListingBridgeSourceLabel(bridgeSourceNavId) || '快捷入库';
  const prepKey = `__prep__${Date.now()}`;
  markListingIngestPending(prepKey, {
    mode: 'ingest',
    total: 0,
    sourceLabel,
    provisional: true,
    preparing: true,
  });
  emitListingIngestProgress(prepKey);

  try {
    // 复色只带入商品库，不绑定自动规则（后续由复色弹窗指定目标 SPU）
    const autoRuleChoice = recolor
      ? { proceed: true, listingAutoRuleId: null, bindListingAutoRule: false }
      : (typeof resolveForIngest === 'function'
        ? await resolveForIngest()
        : { proceed: true, listingAutoRuleId: null, bindListingAutoRule: false });
    if (!autoRuleChoice?.proceed) {
      clearListingIngestPending(prepKey);
      return;
    }

    const ctx = await resolveLaunchContext('platformListing');
    if (!ctx) {
      clearListingIngestPending(prepKey);
      return;
    }

    const sourcingRows = Array.isArray(ctx.sourcingRows) && ctx.sourcingRows.length > 0
      ? ctx.sourcingRows
      : collectSourcingRowsFromBridgeSources(ctx.sources);
    if (sourcingRows.length > 0) {
      clearListingIngestPending(prepKey);
      await runDetailBridgeListingIngest({
        sourcingRows,
        navigate,
        bindListingAutoRule: !!autoRuleChoice.bindListingAutoRule,
        listingAutoRuleId: autoRuleChoice.listingAutoRuleId ?? null,
        listingAutoRuleIds: autoRuleChoice.listingAutoRuleIds ?? null,
        hasTemuRule: !!autoRuleChoice.hasTemuRule,
        recolor,
      });
      return;
    }

    const sources = Array.isArray(ctx?.sources) ? ctx.sources : [];
    if (sources.length === 0) {
      clearListingIngestPending(prepKey);
      return;
    }

    const ingestPayload = {
      sources,
      sourceNavId: bridgeSourceNavId,
      sourceTaskId: ctx.sourceGoodsTaskId || '',
      fromCropTaskId: ctx.fromCropTaskId || '',
      omitTitles: bridgeSourceNavId === 'podProductDesign',
      listingEnrich: ctx.listingEnrich || null,
    };
    await runDetailBridgeListingIngest({
      ingestPayload,
      navigate,
      provisionalKey: prepKey,
      bindListingAutoRule: !!autoRuleChoice.bindListingAutoRule,
      listingAutoRuleId: autoRuleChoice.listingAutoRuleId ?? null,
      listingAutoRuleIds: autoRuleChoice.listingAutoRuleIds ?? null,
      hasTemuRule: !!autoRuleChoice.hasTemuRule,
      recolor,
    });
  } catch (error) {
    clearListingIngestPending(prepKey);
    console.error('写入商品库失败', error);
    toast(error?.message || i18n.t('workbench:ipeUtilError.ingestFailed'), 'error');
  }
}

/** 打开联动弹窗时合并非文本参数；补充说明/修改指令不自动带入 */
function mergeBridgeStudioParamsFromContext(navId, ctx = {}) {
  const raw = ctx.modelStudioParams && typeof ctx.modelStudioParams === 'object'
    ? ctx.modelStudioParams
    : {};
  const { prompt: _p, userPrompt: _u, ...rest } = raw;
  return mergeDetailToolBridgeParams(navId, {}, rest);
}

/**
 * 详情页快捷功能共用控制器：
 * 选工具 → 确认弹窗 → createTask → 5s 可点 Toast → 进功能页（不进详情、不打开历史）。
 *
 * @param {object} [options]
 * @param {(payload: { message: string, type?: string }) => void} [options.setToast]
 * @param {(payload: { taskId: string, groupIndex: number }) => void} [options.onGoodsRemakeStarted]
 * @param {(payload: { taskId: string, groupIndex: number, result: object }) => void} [options.onAiCopywritingRemakeComplete]
 * @param {boolean} [options.enablePodPicker] 是否支持 POD 套图模版选择弹窗
 * @param {(navId: string) => Promise<{
 *   sources?: object[],
 *   prompt?: string,
 *   modelStudioParams?: object|null,
 *   fromCropTaskId?: string,
 *   sourceGoodsTaskId?: string,
 * }|null>} options.resolveLaunchContext 按目标功能解析已选图片与创建参数（弹窗内不会自动带入 prompt / 补充说明）
 * @param {Record<string, () => (boolean|void|Promise<boolean|void>)>} [options.specialHandlers]
 *        页特异旁路（如 platformListing）；返回 false 表示未处理，继续默认流程
 * @param {string} [options.sourceNavId] 当前详情页功能 id，写入商品库时记录来源
 */
export default function useDetailQuickToolBridge({
  setToast,
  onAiCopywritingTaskCreated,
  onGoodsRemakeStarted,
  onAiCopywritingRemakeComplete,
  enablePodPicker = false,
  resolveLaunchContext,
  specialHandlers = {},
  sourceNavId = '',
} = {}) {
  const navigate = useNavigate();
  const [toolBridgeBusy, setToolBridgeBusy] = useState(false);
  const [bridgeLaunch, setBridgeLaunch] = useState(null);
  const [bridgeConfirming, setBridgeConfirming] = useState(false);

  const [podPickerOpen, setPodPickerOpen] = useState(false);
  const [podTemplates, setPodTemplates] = useState([]);
  const [podSelectedTemplateIds, setPodSelectedTemplateIds] = useState([]);
  const [podTemplateGroup, setPodTemplateGroup] = useState('全部');
  const [podCanAccessTeamTemplates, setPodCanAccessTeamTemplates] = useState(false);
  const [podPickerSources, setPodPickerSources] = useState([]);
  const [podFromCropTaskId, setPodFromCropTaskId] = useState('');

  const toast = useCallback((message, type = 'info') => {
    if (setToast) {
      setToast({ message, type });
      return;
    }
    showGlobalToast(message, type);
  }, [setToast]);

  const goodsBridge = useDetailGoodsTemplateBridge({ setToast, onRemakeStarted: onGoodsRemakeStarted });
  const copywritingBridge = useDetailAiCopywritingBridge({
    setToast,
    onTaskCreated: onAiCopywritingTaskCreated,
    onRemakeComplete: onAiCopywritingRemakeComplete,
  });
  const { resolveForIngest, pickModal: listingAutoRulePickModal } = useListingAutoRulePick();

  const bridgeSourceNavId = String(sourceNavId || '').trim();

  const closePodPicker = useCallback(() => {
    if (toolBridgeBusy) return;
    setPodPickerOpen(false);
    setPodSelectedTemplateIds([]);
    setPodPickerSources([]);
    setPodFromCropTaskId('');
  }, [toolBridgeBusy]);

  const confirmPodPicker = useCallback(async () => {
    if (podSelectedTemplateIds.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.selectTemplateSetFirst'), 'warning');
      return;
    }
    const templates = podTemplates.filter((tpl) => (
      podSelectedTemplateIds.includes(String(tpl.id))
      || podSelectedTemplateIds.includes(tpl.id)
    ));
    if (templates.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.selectTemplateSetFirst'), 'warning');
      return;
    }
    if (podPickerSources.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }
    setToolBridgeBusy(true);
    try {
      const created = await createTaskFromDetailToolBridge({
        navId: 'podProductDesign',
        sources: podPickerSources,
        podSelectedTemplates: templates,
        fromCropTaskId: podFromCropTaskId,
      });
      setPodPickerOpen(false);
      setPodPickerSources([]);
      setPodSelectedTemplateIds([]);
      setPodFromCropTaskId('');
      notifyQuickBridgeTaskCreated({
        created,
        fallbackNavId: 'podProductDesign',
        navigate,
      });
    } catch (error) {
      console.error('创建 POD 商品设计任务失败', error);
      toast(error?.message || i18n.t('workbench:ipeUtilError.createFailed'), 'error');
    } finally {
      setToolBridgeBusy(false);
    }
  }, [
    navigate,
    podFromCropTaskId,
    podPickerSources,
    podSelectedTemplateIds,
    podTemplates,
    toast,
  ]);

  const togglePodTemplate = useCallback((tpl) => {
    const id = String(tpl?.id ?? '');
    if (!id) return;
    setPodSelectedTemplateIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const openGoodsStudioCreate = goodsBridge.openGoodsStudioCreate;
  const openAiCopywritingPicker = copywritingBridge.openAiCopywritingPicker;

  const onSelectNav = useCallback(async (targetNavId) => {
    const nav = String(targetNavId || '').trim();
    if (!nav) return;

    if (PLATFORM_LISTING_QUICK_BRIDGE_NAV_IDS.has(nav)
      && !canUsePlatformListingQuickBridge(bridgeSourceNavId)) {
      toast(i18n.t('workbench:ipeUtilError.bridgeWriteUnsupported'), 'warning');
      return;
    }

    const special = specialHandlers?.[nav];
    if (typeof special === 'function') {
      try {
        const handled = await special();
        if (handled !== false) return;
      } catch (error) {
        console.error('快捷功能旁路失败', error);
        toast(error?.message || i18n.t('workbench:ipeUtilError.actionFailed'), 'error');
        return;
      }
    }

    if (typeof resolveLaunchContext !== 'function') {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }

    if ((nav === 'platformListing' || nav === 'platformRecolor')
      && canUsePlatformListingQuickBridge(bridgeSourceNavId)) {
      void runPlatformListingIngestInBackground({
        resolveLaunchContext,
        bridgeSourceNavId,
        navigate,
        toast,
        resolveForIngest,
        recolor: nav === 'platformRecolor',
      });
      return;
    }

    setToolBridgeBusy(true);
    let ctx = null;
    try {
      ctx = await resolveLaunchContext(nav);
    } catch (error) {
      console.error('解析快捷功能选区失败', error);
      toast(error?.message || i18n.t('workbench:ipeUtilError.actionFailed'), 'error');
      setToolBridgeBusy(false);
      return;
    }
    setToolBridgeBusy(false);

    if (ctx == null) return;

    const sources = Array.isArray(ctx?.sources) ? ctx.sources : [];
    if (sources.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }

    if (nav === 'goods') {
      openGoodsStudioCreate(sources, {
        fromCropTaskId: ctx.fromCropTaskId || '',
        sourceGoodsTaskId: ctx.sourceGoodsTaskId || '',
        sourceCopywritingTaskId: ctx.sourceCopywritingTaskId || '',
        // 商品设计 / AI文案：弹窗确认后直接后台生成，不跳转主图创作页手动点「立即生成」
        autoSubmit: bridgeSourceNavId === 'podProductDesign'
          || bridgeSourceNavId === 'aiCopywriting',
      });
      return;
    }

    if (nav === 'aiCopywriting') {
      openAiCopywritingPicker(sources, {
        sourceGoodsTaskId: ctx.sourceGoodsTaskId || '',
      });
      return;
    }

    if (nav === 'podProductDesign') {
      let imageTemplates = [];
      try {
        const loaded = await loadImageTemplatesForDetailBridge();
        imageTemplates = Array.isArray(loaded?.templates) ? loaded.templates : [];
      } catch (error) {
        console.error('加载套图模版失败', error);
        toast(
      error?.message || i18n.t('workbench:ipeUtilError.loadTemplateSetFailed'),
      'error',
    );
      }
      setBridgeLaunch({
        navId: nav,
        sources: sources.map((s) => ({ ...s })),
        studioParams: mergeDetailToolBridgeParams(nav, {}, {}),
        imageTemplates,
        fromCropTaskId: ctx.fromCropTaskId || '',
        sourceNavId: bridgeSourceNavId,
      });
      return;
    }

    const studioParams = mergeBridgeStudioParamsFromContext(nav, ctx);

    setBridgeLaunch({
      navId: nav,
      sources: sources.map((s) => ({ ...s })),
      studioParams,
      fromCropTaskId: ctx.fromCropTaskId || '',
      sourceGoodsTaskId: ctx.sourceGoodsTaskId || '',
      sourceNavId: bridgeSourceNavId,
    });
  }, [
    bridgeSourceNavId,
    navigate,
    openAiCopywritingPicker,
    openGoodsStudioCreate,
    resolveForIngest,
    resolveLaunchContext,
    specialHandlers,
    toast,
  ]);

  const closeBridgeLaunch = useCallback(() => {
    if (bridgeConfirming) return;
    setBridgeLaunch(null);
  }, [bridgeConfirming]);

  const setBridgeLaunchStudioParams = useCallback((patch) => {
    setBridgeLaunch((prev) => (
      prev
        ? {
          ...prev,
          studioParams: mergeDetailToolBridgeParams(prev.navId, prev.studioParams, patch),
        }
        : prev
    ));
  }, []);

  const setBridgeLaunchImageTemplates = useCallback((templates) => {
    setBridgeLaunch((prev) => (
      prev
        ? {
          ...prev,
          imageTemplates: Array.isArray(templates) ? templates : [],
        }
        : prev
    ));
  }, []);

  const confirmBridgeLaunch = useCallback(async () => {
    if (!bridgeLaunch?.navId) return;
    const targetNav = bridgeLaunch.navId;
    const studioParams = bridgeLaunch.studioParams || getDefaultDetailToolBridgeParams(targetNav);
    setBridgeConfirming(true);
    setToolBridgeBusy(true);
    try {
      if (targetNav === 'platformListing') {
        const ingestSourceNav = bridgeLaunch.sourceNavId || bridgeSourceNavId;
        if (!canUsePlatformListingQuickBridge(ingestSourceNav)) {
          toast(i18n.t('workbench:ipeUtilError.bridgeWriteUnsupported'), 'warning');
          return;
        }
        const ingestPayload = {
          sources: bridgeLaunch.sources || [],
          sourceNavId: ingestSourceNav,
          sourceTaskId: bridgeLaunch.sourceGoodsTaskId || '',
          fromCropTaskId: bridgeLaunch.fromCropTaskId || '',
          omitTitles: ingestSourceNav === 'podProductDesign',
          listingEnrich: bridgeLaunch.listingEnrich || null,
        };
        const sourcingRows = collectSourcingRowsFromBridgeSources(bridgeLaunch.sources);
        setBridgeLaunch(null);
        setBridgeConfirming(false);
        setToolBridgeBusy(false);

        void (async () => {
          try {
            const autoRuleChoice = typeof resolveForIngest === 'function'
              ? await resolveForIngest()
              : {
                proceed: true,
                listingAutoRuleId: null,
                listingAutoRuleIds: [],
                bindListingAutoRule: false,
                hasTemuRule: false,
              };
            if (!autoRuleChoice?.proceed) return;
            await runDetailBridgeListingIngest({
              ingestPayload,
              sourcingRows,
              navigate,
              bindListingAutoRule: !!autoRuleChoice.bindListingAutoRule,
              listingAutoRuleId: autoRuleChoice.listingAutoRuleId ?? null,
              listingAutoRuleIds: autoRuleChoice.listingAutoRuleIds ?? null,
              hasTemuRule: !!autoRuleChoice.hasTemuRule,
            });
          } catch (error) {
            console.error('写入商品库失败', error);
            toast(error?.message || i18n.t('workbench:ipeUtilError.ingestFailed'), 'error');
          }
        })();
        return;
      }

      let podSelectedTemplates = null;
      if (targetNav === 'podProductDesign') {
        const ids = (studioParams.imageTemplateIds || []).map(String).filter(Boolean);
        const idSet = new Set(ids);
        let catalog = Array.isArray(bridgeLaunch.imageTemplates)
          ? bridgeLaunch.imageTemplates
          : [];
        podSelectedTemplates = catalog.filter((tpl) => idSet.has(String(tpl?.id)));
        if (podSelectedTemplates.length === 0 && ids.length > 0) {
          const loaded = await loadImageTemplatesForDetailBridge();
          catalog = Array.isArray(loaded?.templates) ? loaded.templates : [];
          podSelectedTemplates = catalog.filter((tpl) => idSet.has(String(tpl?.id)));
        }
        if (podSelectedTemplates.length === 0) {
          throw new Error(i18n.t('workbench:ipeUtilError.needDesignTemplate'));
        }
      }
      const created = await createTaskFromDetailToolBridge({
        navId: targetNav,
        sources: bridgeLaunch.sources || [],
        prompt: String(studioParams.prompt || '').trim(),
        studioParams,
        modelStudioParams: studioParams,
        podSelectedTemplates,
        fromCropTaskId: bridgeLaunch.fromCropTaskId || '',
        sourceGoodsTaskId: bridgeLaunch.sourceGoodsTaskId || '',
      });
      setBridgeLaunch(null);
      notifyQuickBridgeTaskCreated({
        created,
        fallbackNavId: targetNav,
        navigate,
      });
    } catch (error) {
      console.error('功能联动失败', error);
      toast(error?.message || i18n.t('workbench:ipeUtilError.actionFailed'), 'error');
    } finally {
      setBridgeConfirming(false);
      setToolBridgeBusy(false);
    }
  }, [bridgeLaunch, bridgeSourceNavId, navigate, toast, resolveForIngest]);

  const busy = Boolean(
    toolBridgeBusy
    || bridgeConfirming
    || goodsBridge.goodsStudioBusy
    || copywritingBridge.copywritingBusy,
  );

  const isModalOpen = Boolean(
    bridgeLaunch
    || podPickerOpen
    || goodsBridge.goodsStudioModalOpen
    || copywritingBridge.copywritingModalOpen,
  );

  return {
    busy,
    isModalOpen,
    onSelectNav,
    bridgeLaunch,
    bridgeConfirming,
    closeBridgeLaunch,
    setBridgeLaunchStudioParams,
    setBridgeLaunchImageTemplates,
    /** @deprecated 使用 setBridgeLaunchStudioParams */
    setBridgeLaunchPrompt: setBridgeLaunchStudioParams,
    confirmBridgeLaunch,
    enablePodPicker,
    podPickerOpen,
    podTemplates,
    podSelectedTemplateIds,
    podTemplateGroup,
    setPodTemplateGroup,
    podCanAccessTeamTemplates,
    podPickerSources,
    closePodPicker,
    confirmPodPicker,
    togglePodTemplate,
    goodsBridge,
    copywritingBridge,
    listingAutoRulePickModal,
    resolveListingAutoRuleForIngest: resolveForIngest,
  };
}
